<script setup lang="ts">
import { ref } from 'vue';
import DxForm, { DxSimpleItem, DxLabel, DxGroupItem } from 'devextreme-vue/form';
import DxTextBox from 'devextreme-vue/text-box';
import { DxButton } from 'devextreme-vue/button';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { showError, showSuccess, BottoPagePanel } from '@botto/core';
import { fetchSmsReceipt, sendSms, type CreateSmsPayload } from '../../api';

const loading = ref(false);
const messageId = ref<string | null>(null);
const receiptMessageId = ref('');
const receiptResult = ref<unknown>(null);

const form = ref<CreateSmsPayload>({
  system_id: '',
  sender_from: '',
  phone_to: '',
  message: '',
});

async function submitSms() {
  if (!form.value.system_id?.trim() || !form.value.phone_to?.trim() || !form.value.message?.trim()) {
    showError(new Error('Заполните system_id, phone_to и message'));
    return;
  }
  loading.value = true;
  receiptResult.value = null;
  try {
    const res = await sendSms(form.value);
    messageId.value = res.message_id;
    showSuccess(`Отправлено. message_id: ${res.message_id}`);
  } catch (e) {
    showError(e);
  } finally {
    loading.value = false;
  }
}

async function loadReceipt() {
  if (!messageId.value) {
    showError(new Error('Сначала отправьте SMS'));
    return;
  }
  if (!receiptMessageId.value.trim()) {
    showError(new Error('Укажите message_id'));
    return;
  }
  loading.value = true;
  try {
    receiptResult.value = await fetchSmsReceipt(messageId.value, receiptMessageId.value.trim());
    showSuccess('Квитанция получена');
  } catch (e) {
    showError(e);
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <BottoPagePanel title="Отправка SMS" standalone>
    <DxForm :form-data="form" :col-count="2" label-location="top">
      <DxGroupItem :col-span="2" caption="Параметры отправки">
        <DxSimpleItem data-field="system_id" :col-span="2">
          <DxLabel text="System ID" />
        </DxSimpleItem>
        <DxSimpleItem data-field="sender_from">
          <DxLabel text="Отправитель" />
        </DxSimpleItem>
        <DxSimpleItem data-field="phone_to">
          <DxLabel text="Телефон получателя" />
        </DxSimpleItem>
        <DxSimpleItem
          data-field="message"
          editor-type="dxTextArea"
          :col-span="2"
          :editor-options="{ height: 100 }"
        >
          <DxLabel text="Сообщение" />
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>

    <div class="ops-page__actions">
      <DxButton text="Отправить" type="default" :disabled="loading" @click="submitSms" />
    </div>

    <div v-if="messageId" class="validation-box">
      <strong>message_id:</strong> <code>{{ messageId }}</code>
    </div>

    <div v-if="messageId" class="receipt-form">
      <label class="receipt-form__label">message_id (для квитанции)</label>
      <DxTextBox v-model:value="receiptMessageId" />
    </div>

    <div v-if="messageId" class="ops-page__actions">
      <DxButton text="Получить квитанцию" :disabled="loading" @click="loadReceipt" />
    </div>

    <pre v-if="receiptResult != null" class="json-block">{{ JSON.stringify(receiptResult, null, 2) }}</pre>
  </BottoPagePanel>

  <DxLoadPanel :visible="loading" :show-indicator="true" :shading="true" />
</template>

<style scoped>
.ops-page__actions {
  display: flex;
  gap: 8px;
}
.validation-box {
  padding: 10px 12px;
  background: #e8f4e8;
  border-radius: 4px;
  font-size: 14px;
}
.receipt-form {
  max-width: 480px;
}
.json-block {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 12px;
  overflow: auto;
  max-height: 400px;
}
</style>
